import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, BehaviorSubject } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { PlatformService } from '../core/platform/platform.service';
import { StorageService } from '../core/platform/storage.service';

/**
 * Tenant interface
 */
export interface Tenant {
  schema_name: string;
  name: string;
  domain?: string;  // Optional - may not be available during tenant detection
}

/**
 * Tenant detection response
 */
interface TenantDetectionResponse {
  tenant: Tenant | null;
  is_public: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class TenantService {
  private readonly BACKOFFICE_DOMAIN =
    environment.backofficeUrl || 'platform.localhost';
  private readonly MOBILE_TENANT_STORAGE_KEY = 'mobile_active_tenant_schema';
  private currentTenantSubject = new BehaviorSubject<Tenant | null>(null);
  public currentTenant$ = this.currentTenantSubject.asObservable();

  constructor(
    private http: HttpClient,
    private platformService: PlatformService,
    private storageService: StorageService,
  ) {
    // Only detect tenant in browser, not on server
    if (this.platformService.isBrowser()) {
      this.detectCurrentTenant();
    }
  }

  /**
   * Detect current tenant from URL/domain
   * For multi-tenant setup, tenant is determined by:
   * 1. Query parameter ?tenant=schema_name
   * 2. Subdomain (club1.example.com -> schema_name=club1)
   * 3. URL path (example.com/tenant/club1 -> schema_name=club1)
   * 4. Default to null if backoffice/public domain
   */
  detectCurrentTenant(): Observable<TenantDetectionResponse> {
    // Check for query parameter first
    const urlParams = new URLSearchParams(
      this.platformService.getLocationSearch(),
    );
    const tenantParam = urlParams.get('tenant');

    if (tenantParam) {
      const tenant: Tenant = { schema_name: tenantParam, name: tenantParam };
      this.currentTenantSubject.next(tenant);
      this.persistMobileTenant(tenant.schema_name);
      return of({ tenant, is_public: false });
    }

    const mobileStoredTenant = this.getStoredMobileTenant();
    if (mobileStoredTenant) {
      const tenant: Tenant = {
        schema_name: mobileStoredTenant,
        name: mobileStoredTenant,
      };
      this.currentTenantSubject.next(tenant);
      return of({ tenant, is_public: false });
    }

    // Check if we're on backoffice/public domain
    if (this.isBackofficeDomain()) {
      this.currentTenantSubject.next(null);
      return of({ tenant: null, is_public: true });
    }

    // Extract tenant from subdomain
    const tenantFromDomain = this.extractTenantFromDomain();
    if (tenantFromDomain) {
      const tenant: Tenant = {
        schema_name: tenantFromDomain,
        name: tenantFromDomain,
      };
      this.currentTenantSubject.next(tenant);
      return of({ tenant, is_public: false });
    }

    // Default to public/no tenant
    this.currentTenantSubject.next(null);
    return of({ tenant: null, is_public: true });
  }

  /**
   * Get current tenant (if any)
   */
  getCurrentTenant(): Observable<Tenant | null> {
    return this.currentTenant$;
  }

  /**
   * Get current tenant value synchronously
   */
  getCurrentTenantSync(): Tenant | null {
    return this.currentTenantSubject.value;
  }

  /**
   * Check if we're on backoffice/public domain
   * Public domains are:
   * - localhost (no subdomain)
   * - platform.localhost (backoffice)
   * - sportowcy.localhost (public landing)
   */
  private isBackofficeDomain(): boolean {
    const host = this.platformService.getLocationHostname();

    // Exact match for public/backoffice domains
    const publicDomains = [
      'localhost',
      'platform.localhost',
      'sportowcy.localhost',
      'example.com',
      'www.example.com',
      'sportowcy.local',
      'www.sportowcy.local',
    ];

    return publicDomains.includes(host);
  }

  /**
   * Extract tenant schema name from subdomain
   * Examples:
   *   klub1.localhost -> 'klub1'
   *   klub1.example.com -> 'klub1'
   *   club1.sportowie.pl -> 'club1'
   */
  private extractTenantFromDomain(): string | null {
    return this.extractTenantFromHost(
      this.platformService.getLocationHostname(),
    );
  }

  extractTenantFromHost(hostname: string): string | null {
    const parts = hostname.split('.');

    if (parts.length >= 2) {
      const subdomain = parts[0];
      const excludedSubdomains = [
        'www',
        'api',
        'admin',
        'platform',
        'localhost',
        'sportowcy',
      ];
      if (!excludedSubdomains.includes(subdomain)) {
        return subdomain;
      }
    }

    return null;
  }

  setCurrentTenantSchema(schemaName: string | null): void {
    if (!schemaName) {
      this.currentTenantSubject.next(null);
      this.persistMobileTenant(null);
      return;
    }

    const tenant: Tenant = { schema_name: schemaName, name: schemaName };
    this.currentTenantSubject.next(tenant);
    this.persistMobileTenant(schemaName);
  }

  /**
   * Check if current tenant is backoffice/platform
   */
  isBackoffice(): boolean {
    return (
      this.currentTenantSubject.value === null && this.isBackofficeDomain()
    );
  }

  /**
   * Get API base URL for current tenant
   */
  getApiBaseUrl(): string {
    if (!this.platformService.isBrowser()) {
      return `${environment.apiUrl}`;
    }

    const baseUrl = new URL(environment.apiUrl);
    const tenant = this.getCurrentTenantSync();
    const tenantFromDomain = this.extractTenantFromDomain();
    const activeTenant = tenant?.schema_name || tenantFromDomain;
    const tenantHostTemplate = (environment as { tenantHostTemplate?: string })
      .tenantHostTemplate;

    if (
      this.platformService.getRuntimePlatform() === 'capacitor' &&
      activeTenant &&
      tenantHostTemplate &&
      tenantHostTemplate.includes('{tenant}')
    ) {
      const tenantHost = tenantHostTemplate.replace('{tenant}', activeTenant);
      return `${baseUrl.protocol}//${tenantHost}`;
    }

    if (activeTenant) {
      const host = this.platformService.getLocationHostname();
      const port = baseUrl.port ? `:${baseUrl.port}` : '';
      return `${baseUrl.protocol}//${host}${port}`;
    }

    return `${baseUrl.protocol}//${baseUrl.hostname}${baseUrl.port ? `:${baseUrl.port}` : ''}`;
  }

  /**
   * Get full URL for tenant-specific endpoint
   */
  getTenantApiUrl(endpoint: string): string {
    const tenant = this.getCurrentTenantSync();
    const baseUrl = this.getApiBaseUrl();

    if (tenant) {
      // Tenant-specific endpoints could be routed via subdomain or included in request
      return `${baseUrl}${endpoint}`;
    }

    return `${baseUrl}${endpoint}`;
  }

  /**
   * Get tenant header for API requests
   * Some APIs may require explicit tenant header
   */
  getTenantHeaders(): { [key: string]: string } {
    const tenant = this.getCurrentTenantSync();
    if (tenant) {
      return {
        'X-Tenant-Schema': tenant.schema_name,
      };
    }
    return {};
  }

  private getStoredMobileTenant(): string | null {
    if (this.platformService.getRuntimePlatform() !== 'capacitor') {
      return null;
    }
    return this.storageService.getLocalItem(this.MOBILE_TENANT_STORAGE_KEY);
  }

  private persistMobileTenant(schemaName: string | null): void {
    if (this.platformService.getRuntimePlatform() !== 'capacitor') {
      return;
    }

    if (!schemaName) {
      this.storageService.removeLocalItem(this.MOBILE_TENANT_STORAGE_KEY);
      return;
    }

    this.storageService.setLocalItem(
      this.MOBILE_TENANT_STORAGE_KEY,
      schemaName,
    );
  }
}
